import { useEffect, useMemo, useRef, useState } from "react";
import {
  doc,
  onSnapshot,
  type DocumentData,
  type DocumentReference,
  type FirestoreError,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { firestoreErrorMessage } from "./useCollection";

export interface DocState<T> {
  data: (T & { id: string }) | null;
  loading: boolean;
  /** Türkçe, kullanıcıya doğrudan gösterilebilir hata. */
  error: string | null;
  /** Doküman gerçekten yok (yetki hatası DEĞİL). */
  notFound: boolean;
}

type DocBuilder = () => DocumentReference<DocumentData> | null;

/**
 * Tek doküman dinleyicisi.
 *
 * İKİ ÇAĞRI BİÇİMİ:
 *   1) useDoc<T>("products/123/private/cost")
 *   2) useDoc<T>(() => (id ? doc(db,"orders",id) : null), [id])
 *
 * Biçim 2, dokümanın KOŞULLU okunması için gereklidir: maliyet/kâr dokümanı
 * yalnızca financeView varsa dinlenir — yetkisiz rolde sorgu hiç açılmaz.
 *
 * "Yok" ile "yetkin yok" ayrımı kasıtlıdır: private/* dokümanları yetkisiz
 * rolde permission-denied döner; bunu "kayıt bulunamadı" diye göstermek
 * kullanıcıyı yanıltır.
 */
export function useDoc<T = DocumentData>(
  source: string | DocBuilder,
  deps: readonly unknown[] = [],
): DocState<T> {
  const [data, setData] = useState<(T & { id: string }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const sourceRef = useRef<string | DocBuilder>(source);
  sourceRef.current = source;

  const effectDeps: readonly unknown[] = typeof source === "string" ? [source] : deps;

  useEffect(() => {
    const s = sourceRef.current;
    const ref: DocumentReference<DocumentData> | null =
      typeof s === "string" ? (s ? doc(db, s) : null) : s();

    if (!ref) {
      setData(null);
      setLoading(false);
      setError(null);
      setNotFound(false);
      return;
    }

    setLoading(true);
    setError(null);
    setNotFound(false);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setData(null);
          setNotFound(true);
        } else {
          setData({ ...(snap.data() as T), id: snap.id });
          setNotFound(false);
        }
        setLoading(false);
      },
      (e: FirestoreError) => {
        console.error("Firestore doküman hatası", e);
        setError(firestoreErrorMessage(e));
        setData(null);
        setLoading(false);
      },
    );

    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, effectDeps);

  return useMemo(
    () => ({ data, loading, error, notFound }),
    [data, loading, error, notFound],
  );
}
